import {
  type CoreBio,
  type CoreSocialLinks,
  type FBio,
  type FSocialLinks,
  type RecursivePartial,
  SCHEMA_VERSION,
  type UniqueDocIdentifier,
  type UniqueIdentifier,
} from "@piperunner-cms/types/v1";
import { and, collection, doc, getDoc, getDocs, query, setDoc, where } from "firebase/firestore";
import { fsDb } from "@/client/utils/initialize-fs-db";
import { FIRESTORE_COLLECTION_PATH } from "@/constants/paths";
import {
  doesDocExist,
  doesDocumentBelongToUser,
  isDocumentIdentifierPresent,
  isUniqueIdentifierPresent,
  normalizeObject,
} from "@/utils/checks";

export const getSocialLinks = async (identifier: UniqueIdentifier): Promise<FSocialLinks> => {
  const { userId, projectId } = identifier;
  isUniqueIdentifierPresent(userId, projectId);

  const collectionRef = collection(fsDb, FIRESTORE_COLLECTION_PATH.SOCIAL_LINKS);
  const docQuery = query(collectionRef, and(where("userId", "==", userId), where("projectId", "==", projectId)));
  const docSnap = await getDocs(docQuery);

  if (docSnap.empty) {
    console.warn(`Social links document does not exist for userId: ${userId} for projectId: ${projectId}`);

    // create a new document if it does not exist
    const newDocRef = doc(collectionRef);
    const newDocData: FSocialLinks = {
      schemaVersion: SCHEMA_VERSION,
      revision: 1,
      id: newDocRef.id,
      userId,
      projectId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      publishedAt: null,
      latest: null, // This is a design choice since future extensions to the schema will not require a default value
      draft: null,
    };

    await setDoc(newDocRef, newDocData);

    return newDocData;
  }

  return {
    userId,
    projectId,
    schemaVersion: docSnap.docs[0].data().schemaVersion,
    revision: docSnap.docs[0].data().revision,
    id: docSnap.docs[0].id,
    latest: docSnap.docs[0].data().latest as CoreSocialLinks,
    draft: docSnap.docs[0].data().draft as CoreSocialLinks | null,
    createdAt: docSnap.docs[0].data().createdAt,
    updatedAt: docSnap.docs[0].data().updatedAt,
    publishedAt: docSnap.docs[0].data().publishedAt ?? null,
  };
};

export const updateSocialLinks = async (
  identifier: UniqueDocIdentifier,
  data: RecursivePartial<CoreSocialLinks>,
): Promise<void> => {
  const { userId, projectId, id } = identifier;

  isUniqueIdentifierPresent(userId, projectId);
  isDocumentIdentifierPresent(id);

  const docRef = doc(fsDb, FIRESTORE_COLLECTION_PATH.SOCIAL_LINKS, id);

  const docSnap = await getDoc(docRef);

  doesDocExist(docSnap);
  doesDocumentBelongToUser(identifier, docSnap);

  const updatedData = {
    updatedAt: new Date().toISOString(),
    draft: normalizeObject(data),
  } as RecursivePartial<FSocialLinks>;

  await setDoc(docRef, updatedData, { merge: true });
};

export const resetSocialLinks = async (identifier: UniqueDocIdentifier): Promise<void> => {
  const { userId, projectId, id } = identifier;

  isUniqueIdentifierPresent(userId, projectId);
  isDocumentIdentifierPresent(id);

  const docRef = doc(fsDb, FIRESTORE_COLLECTION_PATH.SOCIAL_LINKS, id);

  const docSnap = await getDoc(docRef);

  doesDocExist(docSnap);
  doesDocumentBelongToUser(identifier, docSnap);

  const resetData = {
    draft: null,
  } as RecursivePartial<FSocialLinks>;

  await setDoc(docRef, resetData, { merge: true });
};

/**
 * Publishes social links in Firestore.
 *
 * @param {UniqueIdentifier & { documentId?: string }} identifier - The unique identifier for the user and project, along with the document ID.
 *
 * @returns {Promise<void>} A promise that resolves when the publish is complete.
 * @throws {Error} If the document does not exist or does not belong to the user.
 */
export const publishSocialLinks = async (identifier: UniqueDocIdentifier): Promise<void> => {
  const { userId, projectId, id } = identifier;

  isUniqueIdentifierPresent(userId, projectId);
  isDocumentIdentifierPresent(id);

  const docRef = doc(fsDb, FIRESTORE_COLLECTION_PATH.SOCIAL_LINKS, id);

  const docSnap = await getDoc(docRef);

  doesDocExist(docSnap);
  doesDocumentBelongToUser(identifier, docSnap);

  const updatedData = {
    revision: docSnap.data().revision + 1,
    updatedAt: new Date().toISOString(),
    publishedAt: new Date().toISOString(),
    latest: docSnap.data().draft,
    draft: null,
  } as RecursivePartial<FSocialLinks>;

  await setDoc(docRef, updatedData, { merge: true });
};

export const getBio = async (identifier: UniqueIdentifier): Promise<FBio> => {
  const { userId, projectId } = identifier;
  isUniqueIdentifierPresent(userId, projectId);

  const collectionRef = collection(fsDb, FIRESTORE_COLLECTION_PATH.BIOS);
  const docQuery = query(collectionRef, and(where("userId", "==", userId), where("projectId", "==", projectId)));
  const docSnap = await getDocs(docQuery);

  if (docSnap.empty) {
    console.warn(`Bio document does not exist for userId: ${userId} for projectId: ${projectId}`);

    // create a new document if it does not exist
    const newDocRef = doc(collectionRef);
    const newDocData: FBio = {
      schemaVersion: SCHEMA_VERSION,
      revision: 1,
      id: newDocRef.id,
      userId,
      projectId,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      publishedAt: null,
      latest: null, // This is a design choice since future extensions to the schema will not require a default value
      draft: null,
    };

    await setDoc(newDocRef, newDocData);

    return newDocData;
  }

  return {
    userId,
    projectId,
    schemaVersion: docSnap.docs[0].data().schemaVersion,
    revision: docSnap.docs[0].data().revision,
    id: docSnap.docs[0].id,
    latest: docSnap.docs[0].data().latest as CoreBio,
    draft: docSnap.docs[0].data().draft as CoreBio | null,
    createdAt: docSnap.docs[0].data().createdAt,
    updatedAt: docSnap.docs[0].data().updatedAt,
    publishedAt: docSnap.docs[0].data().publishedAt ?? null,
  };
};

export const updateBio = async (identifier: UniqueDocIdentifier, data: RecursivePartial<CoreBio>): Promise<void> => {
  const { userId, projectId, id } = identifier;

  isUniqueIdentifierPresent(userId, projectId);
  isDocumentIdentifierPresent(id);

  const docRef = doc(fsDb, FIRESTORE_COLLECTION_PATH.BIOS, id);

  const docSnap = await getDoc(docRef);

  doesDocExist(docSnap);
  doesDocumentBelongToUser(identifier, docSnap);

  const updatedData = {
    updatedAt: new Date().toISOString(),
    draft: normalizeObject(data),
  } as RecursivePartial<FBio>;

  await setDoc(docRef, updatedData, { merge: true });
};

export const resetBio = async (identifier: UniqueDocIdentifier): Promise<void> => {
  const { userId, projectId, id } = identifier;

  isUniqueIdentifierPresent(userId, projectId);
  isDocumentIdentifierPresent(id);

  const docRef = doc(fsDb, FIRESTORE_COLLECTION_PATH.BIOS, id);

  const docSnap = await getDoc(docRef);

  doesDocExist(docSnap);
  doesDocumentBelongToUser(identifier, docSnap);

  const resetData = {
    draft: null,
  } as RecursivePartial<FBio>;

  await setDoc(docRef, resetData, { merge: true });
};

export const publishBio = async (identifier: UniqueDocIdentifier): Promise<void> => {
  const { userId, projectId, id } = identifier;

  isUniqueIdentifierPresent(userId, projectId);
  isDocumentIdentifierPresent(id);

  const docRef = doc(fsDb, FIRESTORE_COLLECTION_PATH.BIOS, id);

  const docSnap = await getDoc(docRef);

  doesDocExist(docSnap);
  doesDocumentBelongToUser(identifier, docSnap);

  const updatedData = {
    revision: docSnap.data().revision + 1,
    updatedAt: new Date().toISOString(),
    publishedAt: new Date().toISOString(),
    latest: docSnap.data().draft,
    draft: null,
  } as RecursivePartial<FBio>;

  await setDoc(docRef, updatedData, { merge: true });
};
